import type { GatorProtectPlan } from '../gatorProtect.types';

export type GetSelectedPlanInfoInput = Partial<Omit<GatorProtectPlan, 'invoice'>> & {
  invoice?: unknown;
};

export type SelectedPlanInfo = {
  areDaysExpired: boolean;
  isTrialActive: boolean | undefined;
  isTrialExpired: boolean;
  isProActive: boolean | undefined;
  isProOnMonthlyOrAnnualCycle: boolean;
  isAnnualHostingCycle: boolean;
  isTriennialHostingCycle: boolean;
  isAAClient: boolean;
  hasClientUsedTrial: boolean | undefined;
  isTurbo: boolean;
  hasInvoice: boolean;
  invoiceId?: unknown;
};

const getAreDaysExpired = (selectedPlan: GetSelectedPlanInfoInput): boolean => {
  if (selectedPlan.expire_days != null) {
    return Number.isInteger(selectedPlan.expire_days) && selectedPlan.expire_days <= 0;
  }
  return false;
};

const emptyPlanInfo: SelectedPlanInfo = {
  areDaysExpired: false,
  isTrialActive: false,
  isTrialExpired: false,
  isProActive: false,
  isProOnMonthlyOrAnnualCycle: false,
  isAnnualHostingCycle: false,
  isTriennialHostingCycle: false,
  isAAClient: false,
  hasClientUsedTrial: undefined,
  isTurbo: false,
  hasInvoice: false,
};

export const getSelectedPlanInfo = (
  selectedPlan: GetSelectedPlanInfoInput | null | undefined,
): SelectedPlanInfo => {
  if (!selectedPlan) {
    return { ...emptyPlanInfo };
  }

  const areDaysExpired = getAreDaysExpired(selectedPlan);
  const isTrialActive = selectedPlan.is_trial && !areDaysExpired;
  const isTrialExpired = areDaysExpired;
  const isAAClient = selectedPlan.trial_days === 365;
  const hasClientUsedTrial = selectedPlan.trial_used;
  const isTurbo = Boolean(selectedPlan.is_turbo);
  const hasInvoice = Boolean(selectedPlan.invoice);
  const isProActive = selectedPlan.is_pro;
  const hostingCycle = String(selectedPlan.gatorprotect_hosting_cycle ?? '').trim().toLowerCase();
  const isProOnMonthlyOrAnnualCycle = Boolean(
    isProActive && (hostingCycle === 'monthly' || hostingCycle === 'annually'),
  );
  const isAnnualHostingCycle = Boolean(isProActive && hostingCycle === 'annually');
  const isTriennialHostingCycle = Boolean(isProActive && hostingCycle === 'triennially');

  return {
    areDaysExpired,
    isTrialActive,
    isTrialExpired,
    isProActive,
    isProOnMonthlyOrAnnualCycle,
    isAnnualHostingCycle,
    isTriennialHostingCycle,
    isAAClient,
    hasClientUsedTrial,
    isTurbo,
    hasInvoice,
    invoiceId: selectedPlan.invoice,
  };
};
